import {
  type CSSProperties,
  Fragment,
  type LegacyRef,
  type RefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Popover, Portal, Transition } from "@headlessui/react";
import ChevronDown from "../Atomos/Icons/ChevronDown";
import Search from "../Atomos/Icons/Search";
import { useProfileEditorBrandAccent } from "../../Context/ProfileEditorBrandAccentContext";
import { includesFoldedSearch } from "../../utils/foldSearchText";
import { staffFieldRoot, staffFieldShell } from "./fieldStyles";

type Primitive = string | number;

export interface DropDownOptionBase {
  id: Primitive;
  name: string;
  disabled?: boolean;
}

type SharedProps<T extends DropDownOptionBase> = {
  title?: string;
  options: T[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  emptyStateText?: string;
  noResultsText?: string;
  maxSelections?: number;
  sortByName?: boolean;
  virtualize?: boolean | "auto";
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => Primitive;
  renderInPortal?: boolean;
  className?: string;
  buttonClassName?: string;
  panelClassName?: string;
  optionClassName?: string;
  showAsLabel?: boolean;
};

type SingleSelectProps<T extends DropDownOptionBase> = SharedProps<T> & {
  multiple?: false;
  value: T | Primitive | null;
  onChange: (value: T | null) => void;
};

type MultiSelectProps<T extends DropDownOptionBase> = SharedProps<T> & {
  multiple: true;
  value: (T | Primitive)[];
  onChange: (value: T[]) => void;
};

export type DropDownMultipleProps<T extends DropDownOptionBase> =
  | SingleSelectProps<T>
  | MultiSelectProps<T>;

type ScrollSnapshot = { el: HTMLElement; top: number; left: number }[];

type VirtualRowItem<T> =
  | { kind: "header"; title: string }
  | { kind: "row"; option: T };

type VirtualLayout = {
  totalHeight: number;
  tops: number[];
  heights: number[];
};

type VirtualSlice = {
  totalHeight: number;
  slice: { index: number }[];
  paddingTop: number;
  paddingBottom: number;
};

type PanelMetrics = {
  align: "left" | "right";
  direction: "up" | "down";
  listMaxHeight: number;
  style: CSSProperties;
  shouldOpenUp: boolean;
};

type RenderOptionRowArgs<T extends DropDownOptionBase> = {
  option: T;
  multiple: boolean;
  disabled: boolean;
  hasReachedLimit: boolean;
  optionClassName: string;
  selectedKeys: Set<string>;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => Primitive;
  onToggle: (option: T) => void;
  rowKey: string;
  className?: string;
  style?: CSSProperties;
  filterCheckboxClasses: string;
};

type RenderOptionsArgs<T extends DropDownOptionBase> = {
  multiple: boolean;
  filteredOptions: T[];
  selectedFilteredOptions: T[];
  unselectedFilteredOptions: T[];
  selectedKeys: Set<string>;
  disabled: boolean;
  hasReachedLimit: boolean;
  optionClassName: string;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => Primitive;
  onToggle: (option: T) => void;
  filterCheckboxClasses: string;
};

type RenderVirtualOptionsArgs<T extends DropDownOptionBase> =
  RenderOptionsArgs<T> & {
    flatVirtualItems: VirtualRowItem<T>[];
    virtualLayout: VirtualLayout;
    panelListMaxHeight: number;
    scrollTop: number;
  };

type RenderPanelBodyArgs<T extends DropDownOptionBase> =
  RenderVirtualOptionsArgs<T> & {
    searchable: boolean;
    searchInputRef: RefObject<HTMLInputElement | null>;
    query: string;
    setQuery: (value: string) => void;
    selectionCount: number;
    maxSelections: number;
    onClearAll: () => void;
    options: T[];
    emptyStateText: string;
    noResultsText: string;
    renderInPortal: boolean;
    shouldVirtualize: boolean;
    setScrollTop: (value: number) => void;
    optionsListRef: RefObject<HTMLDivElement>;
  };

const baseInputClasses =
  "w-full rounded-md text-left text-sm transition-colors focus:outline-none";

const FILTER_CHECKBOX_CLASSES_DEFAULT =
  "pointer-events-none relative h-4 w-4 shrink-0 appearance-none border border-neutral-dark bg-white checked:border-Default checked:bg-accent-Default-light checked:after:border-Default after:absolute after:left-1/2 after:top-1/2 after:h-[7px] after:w-[3px] after:-translate-x-1/2 after:-translate-y-[60%] after:rotate-45 after:border-b after:border-r after:border-transparent after:content-['']";

const FILTER_CHECKBOX_CLASSES_BRAND =
  "pointer-events-none relative h-4 w-4 shrink-0 appearance-none border border-neutral-dark bg-white checked:border-primary checked:bg-primary-light checked:after:border-primary after:absolute after:left-1/2 after:top-1/2 after:h-[7px] after:w-[3px] after:-translate-x-1/2 after:-translate-y-[60%] after:rotate-45 after:border-b after:border-r after:border-transparent after:content-['']";
const PANEL_WIDTH_PX = 448;
const VIEWPORT_GUTTER_PX = 16;
const PANEL_VERTICAL_GAP_PX = 8;
const PANEL_META_HEIGHT_WITH_SEARCH_PX = 92;
const PANEL_META_HEIGHT_NO_SEARCH_PX = 48;
const MIN_PANEL_LIST_HEIGHT_PX = 80;
const VIRTUALIZE_THRESHOLD = 300;
const VIRTUAL_ROW_HEIGHT_PX = 40;
const VIRTUAL_HEADER_HEIGHT_PX = 36;
const VIRTUAL_OVERSCAN = 5;
const VIRTUAL_VIEWPORT_HEIGHT_PX = 288;

function captureScrollSnapshot (roots: (HTMLElement | null)[]): ScrollSnapshot {
  const seen = new Set<Element>();
  const entries: ScrollSnapshot = [];

  const push = (el: HTMLElement | null) => {
    if (!el || seen.has(el)) {
      return;
    }
    seen.add(el);
    entries.push({ el, top: el.scrollTop, left: el.scrollLeft });
  };

  for (const root of roots) {
    let node: HTMLElement | null = root;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const scrollableY =
        /(auto|scroll|overlay)/.test(style.overflowY) &&
        node.scrollHeight > node.clientHeight;
      const scrollableX =
        /(auto|scroll|overlay)/.test(style.overflowX) &&
        node.scrollWidth > node.clientWidth;
      if (scrollableY || scrollableX) {
        push(node);
      }
      node = node.parentElement;
    }
  }

  const docEl = document.scrollingElement ?? document.documentElement;
  if (docEl instanceof HTMLElement && !seen.has(docEl)) {
    push(docEl);
  }

  return entries;
}

function restoreScrollSnapshot (entries: ScrollSnapshot) {
  for (const { el, top, left } of entries) {
    el.scrollTop = top;
    el.scrollLeft = left;
  }
}

function virtualItemHeight<T> (item: VirtualRowItem<T>): number {
  return item.kind === "header" ? VIRTUAL_HEADER_HEIGHT_PX : VIRTUAL_ROW_HEIGHT_PX;
}

function extractId<T extends DropDownOptionBase> (
  value: T | Primitive
): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "object") {
    return String(value.id);
  }
  return String(value);
}

function buildOptionsById<T extends DropDownOptionBase> (
  options: T[],
  getOptionValue: (option: T) => Primitive
): Map<string, T> {
  const map = new Map<string, T>();
  for (const option of options) {
    map.set(String(getOptionValue(option)), option);
  }
  return map;
}

function resolveCanonicalOne<T extends DropDownOptionBase> (
  value: T | Primitive | null | undefined,
  byId: Map<string, T>
): T | null {
  if (value === null || value === undefined) {
    return null;
  }
  const id = extractId(value as T | Primitive);
  if (id === null) {
    return null;
  }
  return byId.get(id) ?? null;
}

function resolveCanonicalList<T extends DropDownOptionBase> (
  value: (T | Primitive)[],
  byId: Map<string, T>
): T[] {
  const out: T[] = [];
  for (const item of value) {
    const id = extractId(item);
    if (id === null) {
      continue;
    }
    const option = byId.get(id);
    if (option) {
      out.push(option);
    }
  }
  return out;
}

function clampPanelTop (unclampedTop: number, panelHeight: number) {
  return Math.min(
    Math.max(unclampedTop, VIEWPORT_GUTTER_PX),
    window.innerHeight - VIEWPORT_GUTTER_PX - panelHeight
  );
}

function resolvePanelMetrics (rect: DOMRect, searchable: boolean): PanelMetrics {
  const viewportLeft = VIEWPORT_GUTTER_PX;
  const viewportRight = window.innerWidth - VIEWPORT_GUTTER_PX;
  const availableAbove = rect.top - VIEWPORT_GUTTER_PX;
  const availableBelow = window.innerHeight - rect.bottom - VIEWPORT_GUTTER_PX;
  const panelMetaHeight = searchable
    ? PANEL_META_HEIGHT_WITH_SEARCH_PX
    : PANEL_META_HEIGHT_NO_SEARCH_PX;
  const desiredTotalHeight = panelMetaHeight + VIRTUAL_VIEWPORT_HEIGHT_PX;
  const belowCanFit = availableBelow >= desiredTotalHeight;
  const aboveCanFit = availableAbove >= desiredTotalHeight;
  const isTriggerInLowerHalf = rect.top > window.innerHeight / 2;
  const shouldOpenUp = belowCanFit
    ? false
    : aboveCanFit
      ? true
      : isTriggerInLowerHalf || availableAbove > availableBelow;
  const availableVerticalSpace = shouldOpenUp ? availableAbove : availableBelow;
  const rawListMaxHeight = Math.max(0, availableVerticalSpace - panelMetaHeight);
  const listMaxHeight =
    rawListMaxHeight >= MIN_PANEL_LIST_HEIGHT_PX
      ? Math.min(VIRTUAL_VIEWPORT_HEIGHT_PX, rawListMaxHeight)
      : rawListMaxHeight;
  const totalPanelHeight = panelMetaHeight + listMaxHeight;
  const width = Math.min(
    Math.max(rect.width, PANEL_WIDTH_PX),
    viewportRight - viewportLeft
  );
  const leftIfLeftAligned = rect.left;
  const leftIfRightAligned = rect.right - width;
  const rightOverflowIfLeftAligned = Math.max(
    0,
    leftIfLeftAligned + width - viewportRight
  );
  const leftOverflowIfRightAligned = Math.max(
    0,
    viewportLeft - leftIfRightAligned
  );
  const align =
    rightOverflowIfLeftAligned <= leftOverflowIfRightAligned ? "left" : "right";
  const unclampedLeft =
    align === "right" ? leftIfRightAligned : leftIfLeftAligned;
  const left = Math.min(
    Math.max(unclampedLeft, viewportLeft),
    viewportRight - width
  );
  const unclampedTop = shouldOpenUp
    ? rect.top - totalPanelHeight - PANEL_VERTICAL_GAP_PX
    : rect.bottom + PANEL_VERTICAL_GAP_PX;

  return {
    align,
    direction: shouldOpenUp ? "up" : "down",
    listMaxHeight,
    shouldOpenUp,
    style: {
      left,
      top: clampPanelTop(unclampedTop, totalPanelHeight),
      width,
    },
  };
}

function buildVirtualLayout<T extends DropDownOptionBase> (
  items: VirtualRowItem<T>[]
): VirtualLayout {
  let offset = 0;
  const tops: number[] = [];
  const heights: number[] = [];

  for (const item of items) {
    const height = virtualItemHeight(item);
    tops.push(offset);
    heights.push(height);
    offset += height;
  }

  return { totalHeight: offset, tops, heights };
}

function getVirtualSlice<T extends DropDownOptionBase> (
  items: VirtualRowItem<T>[],
  layout: VirtualLayout,
  panelListMaxHeight: number,
  scrollTop: number
): VirtualSlice {
  if (!items.length) {
    return { totalHeight: 0, slice: [], paddingTop: 0, paddingBottom: 0 };
  }

  const topBound = Math.max(0, scrollTop - VIRTUAL_OVERSCAN * VIRTUAL_ROW_HEIGHT_PX);
  const bottomBound =
    scrollTop + panelListMaxHeight + VIRTUAL_OVERSCAN * VIRTUAL_ROW_HEIGHT_PX;

  let start = 0;
  for (let index = 0;index < layout.tops.length;index += 1) {
    if (layout.tops[index] + layout.heights[index] > topBound) {
      start = index;
      break;
    }
  }

  let end = start;
  for (let index = start;index < layout.tops.length;index += 1) {
    if (layout.tops[index] >= bottomBound) {
      break;
    }
    end = index + 1;
  }

  end = Math.min(items.length, Math.max(end, start + 1));

  const paddingTop = layout.tops[start] ?? 0;
  const lastBottom =
    (layout.tops[end - 1] ?? 0) + (layout.heights[end - 1] ?? 0);
  const paddingBottom = Math.max(0, layout.totalHeight - lastBottom);
  const slice = [];

  for (let index = start;index < end;index += 1) {
    slice.push({ index });
  }

  return {
    totalHeight: layout.totalHeight,
    slice,
    paddingTop,
    paddingBottom,
  };
}

function renderSectionHeader (title: string, bordered = false) {
  return (
    <div
      key={`hdr-${title}`}
      className={`flex items-center gap-2 px-3 py-2 text-xs text-gray-500${bordered ? " border-t border-gray-100" : ""}`}
    >
      <span>{title}</span>
      <span className="h-px flex-1 bg-gray-100" />
    </div>
  );
}

function renderOptionRow<T extends DropDownOptionBase> ({
  option,
  multiple,
  disabled,
  hasReachedLimit,
  optionClassName,
  selectedKeys,
  getOptionLabel,
  getOptionValue,
  onToggle,
  rowKey,
  className,
  style,
  filterCheckboxClasses,
}: RenderOptionRowArgs<T>) {
  const optionKey = String(getOptionValue(option));
  const isSelected = selectedKeys.has(optionKey);
  const isOptionDisabled =
    disabled || option.disabled || (multiple && hasReachedLimit && !isSelected);

  return (
    <button
      key={rowKey}
      type="button"
      onClick={() => onToggle(option)}
      disabled={isOptionDisabled}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${isOptionDisabled ? "cursor-not-allowed text-gray-300" : "cursor-pointer text-gray-font hover:bg-gray-50"}${optionClassName ? ` ${optionClassName}` : ""}${className ? ` ${className}` : ""}`}
      style={style}
    >
      {multiple && (
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          tabIndex={-1}
          className={filterCheckboxClasses}
        />
      )}
      <span className="flex-1 truncate">{getOptionLabel(option)}</span>
    </button>
  );
}

function renderFlatOptions<T extends DropDownOptionBase> (
  args: RenderOptionsArgs<T>
): ReactNode {
  const {
    multiple,
    filteredOptions,
    selectedFilteredOptions,
    unselectedFilteredOptions,
  } = args;

  if (!multiple) {
    return filteredOptions.map((option) =>
      renderOptionRow({
        ...args,
        option,
        rowKey: `single-${String(args.getOptionValue(option))}`,
      })
    );
  }

  const rows: ReactNode[] = [];

  if (selectedFilteredOptions.length > 0) {
    rows.push(renderSectionHeader("Selected filters"));
    for (const option of selectedFilteredOptions) {
      rows.push(
        renderOptionRow({
          ...args,
          option,
          rowKey: `sel-${String(args.getOptionValue(option))}`,
        })
      );
    }
  }

  if (unselectedFilteredOptions.length > 0) {
    rows.push(renderSectionHeader("Available filters", true));
    for (const option of unselectedFilteredOptions) {
      rows.push(
        renderOptionRow({
          ...args,
          option,
          rowKey: `avail-${String(args.getOptionValue(option))}`,
        })
      );
    }
  }

  return rows;
}

function renderVirtualOptions<T extends DropDownOptionBase> (
  args: RenderVirtualOptionsArgs<T>
) {
  const { flatVirtualItems, virtualLayout, panelListMaxHeight, scrollTop } = args;
  const { totalHeight, slice, paddingTop, paddingBottom } = getVirtualSlice(
    flatVirtualItems,
    virtualLayout,
    panelListMaxHeight,
    scrollTop
  );

  return (
    <div style={{ height: totalHeight, position: "relative" }}>
      <div style={{ height: paddingTop }} aria-hidden />
      {slice.map(({ index }) => {
        const item = flatVirtualItems[index];
        const top = virtualLayout.tops[index];
        const height = virtualLayout.heights[index];

        if (item.kind === "header") {
          return (
            <div
              key={`vh-${index}-${item.title}`}
              className={`absolute left-0 right-0 flex items-center gap-2 px-3 py-2 text-xs text-gray-500${item.title === "Available filters" ? " border-t border-gray-100" : ""}`}
              style={{ top, height }}
            >
              <span>{item.title}</span>
              <span className="h-px flex-1 bg-gray-100" />
            </div>
          );
        }

        return (
          <div
            key={`vr-${String(args.getOptionValue(item.option))}-${index}`}
            className="absolute left-0 right-0"
            style={{ top, height }}
          >
            {renderOptionRow({
              ...args,
              option: item.option,
              rowKey: `virtual-${String(args.getOptionValue(item.option))}-${index}`,
              className: "h-full",
            })}
          </div>
        );
      })}
      <div style={{ height: paddingBottom }} aria-hidden />
    </div>
  );
}

function renderPanelBody<T extends DropDownOptionBase> ({
  searchable,
  searchInputRef,
  query,
  setQuery,
  multiple,
  selectionCount,
  maxSelections,
  onClearAll,
  options,
  emptyStateText,
  noResultsText,
  renderInPortal,
  panelListMaxHeight,
  shouldVirtualize,
  setScrollTop,
  optionsListRef,
  ...optionsArgs
}: RenderPanelBodyArgs<T>) {
  return (
    <>
      <div className="flex flex-col gap-2">
        {searchable && (
          <div className="flex items-center bg-gray-50 pl-3">
            <span className="pointer-events-none h-4 w-4 shrink-0 text-gray-font">
              <Search />
            </span>
            <input
              ref={searchInputRef as LegacyRef<HTMLInputElement>}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search..."
              className="w-full border-none bg-transparent !px-3 !py-2 !text-sm text-gray-font focus:outline-none"
            />
          </div>
        )}

        <div className="flex items-center justify-between px-3 py-2 text-sm">
          <span className="text-gray-500">
            {multiple ? `${selectionCount}/${maxSelections} selected` : "Select one option"}
          </span>
          <button
            type="button"
            onClick={onClearAll}
            className="font-medium text-secondary underline transition-colors hover:text-secondary-dark disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
            disabled={selectionCount === 0}
          >
            {multiple ? "Clear all" : "Clear"}
          </button>
        </div>
      </div>

      <div
        ref={optionsListRef}
        className="overflow-y-auto"
        style={{
          maxHeight: renderInPortal ? panelListMaxHeight : VIRTUAL_VIEWPORT_HEIGHT_PX,
        }}
        onScroll={(event) => {
          if (shouldVirtualize) {
            setScrollTop(event.currentTarget.scrollTop);
          }
        }}
      >
        {!options.length && (
          <div className="px-2 py-1 text-sm text-gray-500">{emptyStateText}</div>
        )}

        {!!options.length && !optionsArgs.filteredOptions.length && (
          <div className="px-2 py-1 text-sm text-gray-500">{noResultsText}</div>
        )}

        {!!options.length &&
          !!optionsArgs.filteredOptions.length &&
          (shouldVirtualize
            ? renderVirtualOptions({
              multiple,
              panelListMaxHeight,
              ...optionsArgs,
            })
            : renderFlatOptions({ multiple, ...optionsArgs }))}
      </div>
    </>
  );
}

function DropDownMultiple<T extends DropDownOptionBase> (
  props: DropDownMultipleProps<T>
) {
  const {
    title,
    options,
    placeholder,
    disabled = false,
    required = false,
    searchable = true,
    error = false,
    errorMessage,
    emptyStateText = "No options available",
    noResultsText = "No results found",
    maxSelections: maxSelectionsProp,
    sortByName = true,
    virtualize: virtualizeProp = "auto",
    renderInPortal = false,
    className = "",
    buttonClassName = "",
    panelClassName = "",
    optionClassName = "",
    getOptionLabel: customGetOptionLabel,
    getOptionValue: customGetOptionValue,
    showAsLabel = false,
  } = props;

  const maxSelections = maxSelectionsProp ?? options.length;

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [panelAlign, setPanelAlign] = useState<"left" | "right">("left");
  const [panelDirection, setPanelDirection] = useState<"up" | "down">("down");
  const [panelListMaxHeight, setPanelListMaxHeight] = useState(
    VIRTUAL_VIEWPORT_HEIGHT_PX
  );
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({
    left: 0,
    top: 0,
    width: PANEL_WIDTH_PX,
  });
  const [scrollTop, setScrollTop] = useState(0);

  const triggerRef = useRef<HTMLDivElement>(null);
  const portalPanelRef = useRef<HTMLDivElement | null>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pendingScrollRestoreRef = useRef<ScrollSnapshot | null>(null);

  const getOptionLabel = useCallback(
    (option: T) => customGetOptionLabel?.(option) ?? option.name,
    [customGetOptionLabel]
  );

  const getOptionValue = useCallback(
    (option: T) => customGetOptionValue?.(option) ?? option.id,
    [customGetOptionValue]
  );

  const multiple = props.multiple === true;

  const brandAccent = useProfileEditorBrandAccent();
  const filterCheckboxClasses = brandAccent
    ? FILTER_CHECKBOX_CLASSES_BRAND
    : FILTER_CHECKBOX_CLASSES_DEFAULT;

  const optionsById = useMemo(
    () => buildOptionsById(options, getOptionValue),
    [options, getOptionValue]
  );

  const selectedOptions = useMemo(() => {
    if (multiple) {
      return resolveCanonicalList(props.value, optionsById);
    }

    const selectedOption = resolveCanonicalOne(props.value, optionsById);
    return selectedOption ? [selectedOption] : [];
  }, [multiple, optionsById, props.value]);

  const selectedKeys = useMemo(
    () => new Set(selectedOptions.map((option) => String(getOptionValue(option)))),
    [getOptionValue, selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    const sortedOptions = sortByName
      ? [...options].sort((left, right) =>
        getOptionLabel(left)?.localeCompare(getOptionLabel(right))
      )
      : [...options];
    const normalizedQuery = query.trim();

    if (!searchable || !normalizedQuery) {
      return sortedOptions;
    }

    return sortedOptions.filter((option) =>
      includesFoldedSearch(getOptionLabel(option) ?? "", normalizedQuery)
    );
  }, [getOptionLabel, options, query, searchable, sortByName]);

  const selectedFilteredOptions = useMemo(
    () =>
      filteredOptions.filter((option) =>
        selectedKeys.has(String(getOptionValue(option)))
      ),
    [filteredOptions, getOptionValue, selectedKeys]
  );

  const unselectedFilteredOptions = useMemo(
    () =>
      filteredOptions.filter(
        (option) => !selectedKeys.has(String(getOptionValue(option)))
      ),
    [filteredOptions, getOptionValue, selectedKeys]
  );

  const flatVirtualItems = useMemo(() => {
    const items: VirtualRowItem<T>[] = [];

    if (!multiple) {
      for (const option of filteredOptions) {
        items.push({ kind: "row", option });
      }
      return items;
    }

    if (selectedFilteredOptions.length > 0) {
      items.push({ kind: "header", title: "Selected filters" });
      for (const option of selectedFilteredOptions) {
        items.push({ kind: "row", option });
      }
    }

    if (unselectedFilteredOptions.length > 0) {
      items.push({ kind: "header", title: "Available filters" });
      for (const option of unselectedFilteredOptions) {
        items.push({ kind: "row", option });
      }
    }

    return items;
  }, [
    filteredOptions,
    multiple,
    selectedFilteredOptions,
    unselectedFilteredOptions,
  ]);

  const shouldVirtualize =
    virtualizeProp === true ||
    (virtualizeProp === "auto" && flatVirtualItems.length > VIRTUALIZE_THRESHOLD);

  const virtualLayout = useMemo(
    () => buildVirtualLayout(flatVirtualItems),
    [flatVirtualItems]
  );

  useEffect(() => {
    setScrollTop(0);
  }, [query, isOpen, flatVirtualItems.length, shouldVirtualize]);

  useLayoutEffect(() => {
    if (!isOpen || !searchable || disabled) {
      return;
    }

    let focusSearchInputFrameId = 0;
    const waitForDropdownPaintFrameId = requestAnimationFrame(() => {
      focusSearchInputFrameId = requestAnimationFrame(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      });
    });

    return () => {
      cancelAnimationFrame(waitForDropdownPaintFrameId);
      cancelAnimationFrame(focusSearchInputFrameId);
    };
  }, [isOpen, searchable, disabled]);

  const selectionCount = selectedOptions.length;
  const hasReachedLimit = multiple && selectionCount >= maxSelections;

  const displayValue = useMemo(() => {
    if (!selectionCount) {
      return placeholder ?? (multiple ? "Select Multiple" : "Select One");
    }

    return selectedOptions.map((option) => getOptionLabel(option)).join(", ");
  }, [getOptionLabel, multiple, placeholder, selectedOptions, selectionCount]);

  const renderAsLabel = showAsLabel && disabled;
  const labelDisplayValue = selectionCount
    ? selectedOptions.map((option) => getOptionLabel(option)).join(", ")
    : "-";

  const handleClearAll = () => {
    if (disabled) {
      return;
    }

    if (multiple) {
      props.onChange([]);
      return;
    }

    props.onChange(null);
  };

  const toggleOption = (option: T, close: () => void) => {
    if (disabled || option.disabled) {
      return;
    }

    const optionKey = String(getOptionValue(option));
    const isSelected = selectedKeys.has(optionKey);

    pendingScrollRestoreRef.current = captureScrollSnapshot([
      triggerRef.current,
      optionsListRef.current,
    ]);

    if (!multiple) {
      props.onChange(isSelected ? null : option);
      close();
      return;
    }

    if (isSelected) {
      props.onChange(
        selectedOptions.filter(
          (selectedOption) => String(getOptionValue(selectedOption)) !== optionKey
        )
      );
      return;
    }

    if (selectedOptions.length >= maxSelections) {
      pendingScrollRestoreRef.current = null;
      return;
    }

    props.onChange([...selectedOptions, option]);
  };

  useLayoutEffect(() => {
    const snapshot = pendingScrollRestoreRef.current;
    if (!snapshot) {
      return;
    }

    pendingScrollRestoreRef.current = null;
    restoreScrollSnapshot(snapshot);
  });

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return;
    }

    let remeasureRaf1 = 0;
    let remeasureRaf2 = 0;
    let resizeObserverRaf = 0;
    let attachRaf = 0;

    const updatePanelLayout = (alreadyRetried: boolean) => {
      const trigger = triggerRef.current;
      if (!trigger) {
        return;
      }

      cancelAnimationFrame(remeasureRaf1);
      cancelAnimationFrame(remeasureRaf2);

      const metrics = resolvePanelMetrics(
        trigger.getBoundingClientRect(),
        searchable
      );

      setPanelAlign(metrics.align);
      setPanelDirection(metrics.direction);
      setPanelListMaxHeight(metrics.listMaxHeight);

      if (!renderInPortal) {
        return;
      }

      const panel = portalPanelRef.current;
      const rect = trigger.getBoundingClientRect();
      const rawHeight = panel?.getBoundingClientRect().height ?? 0;
      const measuredHeight = rawHeight > 1 ? rawHeight : null;

      if (measuredHeight != null) {
        const top = clampPanelTop(
          metrics.shouldOpenUp
            ? rect.top - measuredHeight - PANEL_VERTICAL_GAP_PX
            : rect.bottom + PANEL_VERTICAL_GAP_PX,
          measuredHeight
        );
        setPanelStyle({
          left: metrics.style.left,
          width: metrics.style.width,
          top,
        });
        return;
      }

      setPanelStyle(metrics.style);
      if (alreadyRetried) {
        return;
      }

      remeasureRaf1 = requestAnimationFrame(() => {
        remeasureRaf2 = requestAnimationFrame(() => {
          updatePanelLayout(true);
        });
      });
    };

    const onResize = () => updatePanelLayout(false);
    const onScroll = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Node &&
        portalPanelRef.current &&
        portalPanelRef.current.contains(target)
      ) {
        return;
      }
      updatePanelLayout(false);
    };

    updatePanelLayout(false);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    let resizeObserver: ResizeObserver | null = null;
    const attachResizeObserver = (attempt: number) => {
      if (!renderInPortal) {
        return;
      }

      const panel = portalPanelRef.current;
      if (panel && typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(resizeObserverRaf);
          resizeObserverRaf = requestAnimationFrame(() => {
            updatePanelLayout(false);
          });
        });
        resizeObserver.observe(panel);
        return;
      }
      if (attempt < 12) {
        attachRaf = requestAnimationFrame(() =>
          attachResizeObserver(attempt + 1)
        );
      }
    };
    attachResizeObserver(0);

    return () => {
      cancelAnimationFrame(remeasureRaf1);
      cancelAnimationFrame(remeasureRaf2);
      cancelAnimationFrame(resizeObserverRaf);
      cancelAnimationFrame(attachRaf);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, renderInPortal, searchable]);

  const panelBody = (close: () => void) =>
    renderPanelBody({
      searchable,
      searchInputRef,
      query,
      setQuery,
      multiple,
      selectionCount,
      maxSelections,
      onClearAll: handleClearAll,
      options,
      emptyStateText,
      noResultsText,
      renderInPortal,
      panelListMaxHeight,
      shouldVirtualize,
      setScrollTop,
      optionsListRef,
      filteredOptions,
      selectedFilteredOptions,
      unselectedFilteredOptions,
      selectedKeys,
      disabled,
      hasReachedLimit,
      optionClassName,
      getOptionLabel,
      getOptionValue,
      onToggle: (option) => toggleOption(option, close),
      flatVirtualItems,
      virtualLayout,
      scrollTop,
      filterCheckboxClasses,
    });

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title && (
        <label className="font-semibold text-gray-font">
          {title}
          {required && <span className="text-primary"> *</span>}
        </label>
      )}

      {renderAsLabel ? (
        <p className="text-base text-gray-font">{labelDisplayValue}</p>
      ) : (
        <Popover
          className={`relative ${staffFieldShell} ${error
            ? "bg-gray-25 outline outline-2 outline-primary"
            : disabled
              ? "bg-gray-100 outline outline-2 outline-gray-200"
              : "bg-gray-25"
            }`}
        >
          {({ open, close }) => (
            <>
              {open !== isOpen && setIsOpen(open)}
              {multiple && selectionCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary p-1 text-xs font-semibold text-white">
                  {selectionCount}
                </span>
              )}

              <Popover.Button
                disabled={disabled}
                className={`${baseInputClasses} ${error
                  ? "bg-gray-25 text-primary"
                  : disabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400"
                    : "bg-gray-25 text-input-gray"
                  } ${buttonClassName}`}
              >
                <div ref={triggerRef} className="relative flex items-center gap-3">
                  <span
                    className={`block flex-1 truncate ${selectionCount && !disabled ? "text-gray-font" : "text-gray-400"}`}
                  >
                    {displayValue}
                  </span>

                  <span
                    className={`absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 ${open ? "rotate-180" : ""} ${disabled ? "text-gray-300" : "text-gray-font"}`}
                  >
                    <ChevronDown />
                  </span>
                </div>
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom={
                  panelDirection === "up"
                    ? "opacity-0 -translate-y-1"
                    : "opacity-0 translate-y-1"
                }
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo={
                  panelDirection === "up"
                    ? "opacity-0 -translate-y-1"
                    : "opacity-0 translate-y-1"
                }
                afterLeave={() => setQuery("")}
              >
                {renderInPortal ? (
                  <Portal>
                    <Popover.Panel
                      ref={portalPanelRef}
                      className={`fixed z-[10020] overflow-hidden rounded-md border border-gray-100 bg-white shadow-xl ${panelClassName}`}
                      style={panelStyle}
                    >
                      {panelBody(close)}
                    </Popover.Panel>
                  </Portal>
                ) : (
                  <Popover.Panel
                    className={`absolute z-50 mt-2 min-w-full w-[28rem] max-w-[calc(100vw-6rem)] overflow-hidden rounded-md border border-gray-100 bg-white shadow-xl ${panelAlign === "right" ? "right-0" : "left-0"} ${panelClassName}`}
                  >
                    {panelBody(close)}
                  </Popover.Panel>
                )}
              </Transition>
            </>
          )}
        </Popover>
      )}

      {error && errorMessage && !renderAsLabel && (
        <span className="text-sm text-primary">{errorMessage}</span>
      )}
    </div>
  );
}

export default DropDownMultiple;
